import React from 'react';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Divider from '@mui/material/Divider';
import InputAdornment from '@mui/material/InputAdornment';
import CircularProgress from '@mui/material/CircularProgress';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import CloseIcon from '@mui/icons-material/Close';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';

interface QuotationSummaryCardProps {
  descuento: number;
  onDescuentoChange: (v: number) => void;
  subtotal: number;
  onGuardar: () => void;
  guardando: boolean;
  guardado: boolean;
  soloLectura?: boolean;
  referencias: number;
  unidadesTotales: number;
}

export function QuotationSummaryCard({
  descuento,
  onDescuentoChange,
  subtotal,
  onGuardar,
  guardando,
  guardado,
  soloLectura = false,
  referencias,
  unidadesTotales,
}: QuotationSummaryCardProps) {
  const descuentoAplicado = Math.round(subtotal * descuento / 100);
  const totalVenta = subtotal - descuentoAplicado;

  const handleDescuentoChange = (raw: string) => {
    const v = parseFloat(raw);
    if (!isNaN(v) && v >= 0 && v <= 100) onDescuentoChange(v);
    else if (raw === '') onDescuentoChange(0);
  };

  return (
    <Card
      data-testid="quotation-summary"
      elevation={0}
      sx={{
        mt: 3,
        borderRadius: '16px',
        background: 'linear-gradient(135deg, #F9F1FD 0%, #F1E8F8 100%)',
        border: '1px solid #E0CEF0',
        px: 3,
        py: 2.5,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 3,
          flexWrap: 'wrap',
          justifyContent: 'flex-end',
        }}
      >
        {/* Referencias */}
        <Box sx={{ textAlign: 'right' }}>
          <Typography sx={{ fontSize: 14, color: '#8A8F99', fontWeight: 500, mb: 0.3 }}>
            Referencias
          </Typography>
          <Typography data-testid="quotation-referencias" sx={{ fontSize: 22, fontWeight: 700, color: '#231F2B' }}>
            {referencias}
          </Typography>
        </Box>

        <Divider orientation="vertical" flexItem sx={{ borderColor: '#D4B8E8' }} />

        {/* Unidades totales */}
        <Box sx={{ textAlign: 'right' }}>
          <Typography sx={{ fontSize: 14, color: '#8A8F99', fontWeight: 500, mb: 0.3 }}>
            Unidades totales
          </Typography>
          <Typography data-testid="quotation-unidades-totales" sx={{ fontSize: 22, fontWeight: 700, color: '#231F2B' }}>
            {unidadesTotales.toLocaleString('es-CO')}
          </Typography>
        </Box>

        <Divider orientation="vertical" flexItem sx={{ borderColor: '#D4B8E8' }} />

        {/* Subtotal */}
        <Box sx={{ textAlign: 'right' }}>
          <Typography sx={{ fontSize: 14, color: '#8A8F99', fontWeight: 500, mb: 0.3 }}>
            Subtotal de venta
          </Typography>
          <Typography data-testid="quotation-subtotal" sx={{ fontSize: 22, fontWeight: 700, color: '#231F2B' }}>
            $ {subtotal.toLocaleString('es-CO')}
          </Typography>
        </Box>

        <Divider orientation="vertical" flexItem sx={{ borderColor: '#D4B8E8' }} />

        {/* Descuento */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography sx={{ fontSize: 15, color: '#5F6673', whiteSpace: 'nowrap' }}>
            Descuento
          </Typography>
          <TextField
            size="small"
            value={descuento > 0 ? descuento.toString() : ''}
            onChange={(e) => handleDescuentoChange(e.target.value)}
            placeholder="0"
            inputProps={{ style: { fontSize: 16, fontWeight: 600, padding: '4px 6px', width: 40, textAlign: 'right' } }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <Typography sx={{ fontSize: 15, color: '#5F6673' }}>%</Typography>
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '8px',
                background: '#FFFFFF',
                '& fieldset': { borderColor: '#D4B8E8' },
              },
            }}
          />
          {descuento > 0 && (
            <IconButton size="small" onClick={() => onDescuentoChange(0)} sx={{ p: 0.3 }} aria-label="limpiar descuento">
              <CloseIcon sx={{ fontSize: 17, color: '#8A8F99' }} />
            </IconButton>
          )}
        </Box>

        {/* Total final */}
        <Box sx={{ textAlign: 'right' }}>
          <Typography sx={{ fontSize: 14, color: '#8A8F99', fontWeight: 500, mb: 0.3 }}>
            Total{descuento > 0 ? ` (−${descuento}%)` : ''}
          </Typography>
          <Typography data-testid="quotation-total" sx={{ fontSize: 27, fontWeight: 800, color: '#A3489D' }}>
            $ {totalVenta.toLocaleString('es-CO')}
          </Typography>
        </Box>

        <Divider orientation="vertical" flexItem sx={{ borderColor: '#D4B8E8' }} />

        {/* Guardar */}
        <Button
          data-testid="btn-guardar-cotizacion"
          variant="contained"
          startIcon={guardando ? <CircularProgress size={14} sx={{ color: '#FFFFFF' }} /> : <SaveOutlinedIcon />}
          onClick={onGuardar}
          disabled={guardando || soloLectura}
          sx={{
            borderRadius: '12px',
            textTransform: 'none',
            fontWeight: 700,
            fontSize: 17,
            px: 3,
            py: 1.2,
            background: '#A3489D',
            '&:hover': { background: '#8B3A84' },
            '&.Mui-disabled': { background: '#D9C6D8', color: '#FFFFFF' },
            whiteSpace: 'nowrap',
          }}
        >
          {guardando ? 'Guardando…' : 'Guardar Cotización'}
        </Button>
      </Box>

      {/* Feedback de éxito */}
      <Snackbar
        open={guardado}
        autoHideDuration={3000}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          data-testid="save-success-feedback"
          severity="success"
          sx={{ borderRadius: '10px', fontWeight: 600, fontSize: 16 }}
        >
          ¡Cotización guardada exitosamente!
        </Alert>
      </Snackbar>
    </Card>
  );
}
